import {
  useAdFleetMapVehicles,
  useFlatTireSimulatedVehicleIds,
} from '@backoffice-frontend/ad-fleet-map'
import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import { FccAdAlertStatus, FccAdAlertType } from '@backoffice-frontend/graphql'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { LoadingIndicator, Text } from '@moia-dev/pace-web'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AdAlertFeedCard } from '../AdAlertFeedCard/AdAlertFeedCard'
import { useAlertSounds } from '../AlertSoundsToggle/useAlertSounds'
import { DRAWER_WIDTH } from '../constants'
import { FlatTireAlertFeedCard } from '../FlatTireAlertFeedCard/FlatTireAlertFeedCard'

const OPERATOR_INITIATED_ALERTS: FccAdAlertType[] = [
  FccAdAlertType.OperatorToCabinCall,
  FccAdAlertType.OutgoingOperatorToSafetyDriverCall,
]

export const AdAlertFeed = () => {
  const { t } = useTranslation()
  const { vehicles, loadingVehicles } = useAdFleetMapVehicles()
  const simulatedVehicleIds = useFlatTireSimulatedVehicleIds()
  const { user } = useBackofficeLoggedInUser()
  const { isBusy } = useAdOperatorAvailability()
  const { playAlertSound } = useAlertSounds()

  const prevVehicleIdsRef = useRef<Set<string>>(new Set())
  const hasInitializedRef = useRef(false)

  const vehiclesWithAlerts = useMemo(() => {
    return vehicles
      .filter(vehicle => {
        const alerts = vehicle.fccCurrentAdAlerts?.alerts ?? []
        const activeAlerts = alerts.filter(
          alert => alert.status !== FccAdAlertStatus.Closed,
        )
        return activeAlerts.length > 0
      })
      .sort((a, b) => {
        const aOldestAlert = getOldestAlertDate(a)
        const bOldestAlert = getOldestAlertDate(b)
        return aOldestAlert - bOldestAlert
      })
  }, [vehicles])

  const isCurrentUserBusy = useMemo(() => {
    return (
      isBusy ||
      vehiclesWithAlerts.some(v => v.fccHandledByOperator?.id === user?.id)
    )
  }, [vehiclesWithAlerts, user?.id, isBusy])

  useEffect(() => {
    if (loadingVehicles) return

    const currentIds = new Set(vehiclesWithAlerts.map(v => v.id))
    const prevIds = prevVehicleIdsRef.current

    const newVehicles = vehiclesWithAlerts.filter(v => !prevIds.has(v.id))

    const hasNewNonUserInitiatedAlerts = newVehicles.some(vehicle => {
      const alerts = vehicle.fccCurrentAdAlerts?.alerts ?? []
      const activeAlerts = alerts.filter(
        alert => alert.status !== FccAdAlertStatus.Closed,
      )
      return activeAlerts.some(
        alert => !OPERATOR_INITIATED_ALERTS.includes(alert.type),
      )
    })

    if (hasNewNonUserInitiatedAlerts && hasInitializedRef.current && !isBusy) {
      playAlertSound()
    }

    hasInitializedRef.current = true
    prevVehicleIdsRef.current = currentIds
  }, [vehiclesWithAlerts, playAlertSound, loadingVehicles, isBusy])

  if (loadingVehicles) {
    return (
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space2,
        padding: theme.space.Space2,
        overflowY: 'auto',
        overflowX: 'hidden',
        flexGrow: 1,
      })}
    >
      <AnimatePresence mode="popLayout">
        {[...simulatedVehicleIds].map(vehicleId => (
          <motion.div
            key={`flat-tire-${vehicleId}`}
            initial={{ x: -DRAWER_WIDTH, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -DRAWER_WIDTH, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            layout
          >
            <FlatTireAlertFeedCard vehicleId={vehicleId} />
          </motion.div>
        ))}
        {vehiclesWithAlerts.map(vehicle => (
          <motion.div
            key={vehicle.id}
            initial={{ x: -DRAWER_WIDTH, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -DRAWER_WIDTH, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            layout
          >
            <AdAlertFeedCard
              vehicle={vehicle}
              isCurrentUserBusy={isCurrentUserBusy}
            />
          </motion.div>
        ))}
      </AnimatePresence>
      {vehiclesWithAlerts.length === 0 && simulatedVehicleIds.size === 0 && (
        <div
          css={theme => ({
            position: 'absolute',
            top: '40%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            display: 'flex',
            justifyContent: 'center',
            padding: theme.space.Space2,
            pointerEvents: 'none',
          })}
        >
          <Text
            size="sm"
            css={theme => ({ color: theme.semantic.ColorContentReduced })}
          >
            {t('adOperatorDrawer_alertFeed_noAlerts_label')}
          </Text>
        </div>
      )}
    </div>
  )
}

const getOldestAlertDate = (vehicle: {
  fccCurrentAdAlerts?: {
    alerts: Array<{ createdAt: string; status: FccAdAlertStatus }>
  } | null
}): number => {
  const alerts = vehicle.fccCurrentAdAlerts?.alerts ?? []
  const activeAlerts = alerts.filter(
    alert => alert.status !== FccAdAlertStatus.Closed,
  )

  if (activeAlerts.length === 0) return Date.now()

  const oldestDate = activeAlerts.reduce((oldest, alert) => {
    const alertDate = new Date(alert.createdAt).getTime()
    return alertDate < oldest ? alertDate : oldest
  }, Date.now())

  return oldestDate
}
